/**
 * Application configuration and constants for the IAP login page.
 *
 * Environment variables are loaded from Vite and used to configure
 * Firebase, MFA behavior, and redirect URLs.
 */

import { stringToBoolean } from 'utils/utils';

/**
 * Firebase and application configuration from environment variables.
 */
export const CONFIG = {
    /** Firebase Web API Key */
    FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
    /** Firebase Project ID */
    FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    /** Default application URL for redirects */
    DEFAULT_APP_URL: import.meta.env.VITE_GOBO_URL || '/',
    /** Globally disable MFA enrollment requirement */
    DISABLE_MFA_ENROLLMENT: stringToBoolean(import.meta.env.VITE_DISABLE_MFA_ENROLLMENT),
    /** Comma-separated list of emails exempt from MFA */
    MFA_EXEMPT_EMAILS: (import.meta.env.VITE_DISABLE_MFA_ENROLLMENT_EMAIL_LIST || '')
        .split(',')
        .map(email => email.trim().toLowerCase())
        .filter(Boolean),
    /** Disable reCAPTCHA for testing */
    DISABLE_RECAPTCHA: stringToBoolean(import.meta.env.VITE_DISABLE_APP_VERIFICATION_FOR_TESTING),
    /** Maintenance status URL */
    MAINTENANCE_URL: import.meta.env.VITE_GOBO_MAINTENANCE_URL,
};

/**
 * Authentication step states for the login flow state machine.
 * @readonly
 * @enum {string}
 */
export const AUTH_STEPS = {
    /** Initial email entry step */
    EMAIL_STEP: 'EMAIL_STEP',
    /** Password entry for email/password users */
    PASSWORD_STEP: 'PASSWORD_STEP',
    /** Provider selection (Google, SAML, OIDC buttons) */
    PROVIDER_SELECTION: 'PROVIDER_SELECTION',
    /** MFA code verification */
    MFA_VERIFICATION: 'MFA_VERIFICATION',
    /** MFA enrollment flow */
    MFA_ENROLLMENT: 'MFA_ENROLLMENT',
    /** User blocked - MFA required but no phone number */
    MFA_BLOCKED: 'MFA_BLOCKED',
    /** Government consent dialog */
    CONSENT_DIALOG: 'CONSENT_DIALOG',
    /** Post-auth loading/redirect */
    LOADING: 'LOADING',
    /** Error state with retry */
    ERROR: 'ERROR',
    /** Sign out completed */
    SIGNED_OUT: 'SIGNED_OUT',
};

/**
 * Firebase Auth error codes mapped to user-friendly messages.
 * Messages are designed to be helpful without revealing security information.
 * @type {Object.<string, string>}
 */
export const ERROR_MESSAGES = {
    // Generic auth errors (intentionally vague for security - FR-3.1.4)
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-not-found': 'Invalid email address or password.',
    'auth/wrong-password': 'Invalid email address or password.',
    'auth/invalid-credential': 'Invalid email address or password.',
    'auth/invalid-login-credentials': 'Invalid email address or password.',

    // Account status errors
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/account-exists-with-different-credential': 'An account already exists with a different sign-in method.',

    // Popup/redirect errors
    'auth/popup-closed-by-user': 'Please complete signing in without closing the popup window.',
    'auth/popup-blocked': 'Popup was blocked. Please allow popups for this site and try again.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',

    // MFA errors
    'auth/invalid-verification-code': 'Invalid verification code. Please check and try again.',
    'auth/code-expired': 'The verification code has expired. Please request a new code.',
    'auth/missing-phone-number': 'Your account does not have a phone number configured for MFA.',
    'auth/invalid-phone-number': 'The phone number associated with your account is invalid.',
    'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
    'auth/quota-exceeded': 'SMS quota exceeded. Please try again later.',

    // Session errors
    'auth/session-expired': 'Your session has expired. Please sign in again.',
    'auth/network-request-failed': 'Network error. Please check your connection and try again.',

    // Token errors
    'auth/id-token-expired': 'Authentication token has expired. Please sign in again.',
    'auth/requires-recent-login': 'This operation requires recent authentication. Please sign in again.',

    // Default fallback
    default: 'An unexpected error occurred. Please try again.',
};

/**
 * Provider type identifiers for sign-in method detection.
 * @readonly
 * @enum {string}
 */
export const PROVIDER_TYPES = {
    PASSWORD: 'password',
    GOOGLE: 'google.com',
    PHONE: 'phone',
    SAML_PREFIX: 'saml.',
    OIDC_PREFIX: 'oidc.',
};

/**
 * Loading message templates for different authentication states.
 * @type {Object.<string, string>}
 */
export const LOADING_MESSAGES = {
    CHECKING_EMAIL: 'Checking your account...',
    SIGNING_IN: 'Signing you in...',
    SIGNING_IN_GOOGLE: 'Complete signing in with Google...',
    SIGNING_IN_SAML: 'Complete signing in with your organization...',
    SIGNING_IN_OIDC: 'Complete signing in...',
    VERIFYING_CODE: 'Verifying code...',
    ENROLLING_MFA: 'Setting up multi-factor authentication...',
    REDIRECTING: 'Redirecting to application...',
    SIGNING_OUT: 'Signing out...',
};

/**
 * US Government System Usage consent text (FR-2.3.1).
 * @type {string[]}
 */
export const CONSENT_WARNINGS = [
    'You are accessing a U.S. Government computer/information system. Access to this system is restricted to U.S. Government-authorized use only.',
    'Unauthorized or improper use of this system may result in disciplinary action, as well as civil and criminal penalties.',
    'By using this information system, you understand and consent to the following:',
];

/**
 * Nested consent items under the third warning.
 * @type {string[]}
 */
export const CONSENT_SUBITEMS = [
    'You have no reasonable expectation of privacy regarding any communications or data transiting or stored on this information system.',
    'At any time, the government may monitor, audit, intercept, search and seize any communication or data transiting or stored on this information system.',
    'Any communications or data transiting or stored on this information system may be disclosed or used for any lawful government purpose.',
];

/**
 * Gets a user-friendly error message for a Firebase error code.
 *
 * @param {string} errorCode - Firebase error code (e.g., 'auth/wrong-password')
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (errorCode) => {
    return ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.default;
};

/**
 * Checks if an email is exempt from MFA requirements.
 *
 * @param {string} email - Email address to check
 * @returns {boolean} True if exempt from MFA
 */
export const isEmailExemptFromMFA = (email) => {
    if (CONFIG.DISABLE_MFA_ENROLLMENT) {
        return true;
    }
    return CONFIG.MFA_EXEMPT_EMAILS.includes(email?.toLowerCase());
};
